import { convertYieldWithNominalWeight, convert, inferUnit } from 'util/unitConversion';
// The number of points on the curve.
const RESOLUTION = 50;

export type DensityCurve = Array<{ [label: string]: number }>;

/**
 * Calculates the estimated yield for a given seeding rate based on the Hardin Model
 * This model requires the use of metric units.
 * @param number seedingRate (seeds per hectare)
 * @param number targetYield (kg per hectare)
 * @param number hardinParamsA (unitless)
 * @param number hardinParamsB (unitless)
 * @returns number (kg per hectare)
 */
export const getEstimatedYield = (
  seedingRate: number,
  targetYield: number,
  hardinParamsA: number,
  hardinParamsB: number
) => {
  // Optimal seeding rate using yield model => my / get - recommended - po
  // since the model is trained with 1000 seeds we need to multiply by 1000
  const optimalSeedingRate = 1000 * Math.exp(hardinParamsA + hardinParamsB * Math.log(targetYield));
  const beta1 = -1 / optimalSeedingRate;
  const beta0 = Math.log(targetYield) - Math.log(optimalSeedingRate) + 1;
  return seedingRate * Math.exp(beta0 + beta1 * seedingRate);
};

/**
 * Calculates a density curve based on the Hardin model, it allows you to override the name for the yield key
 * @param {Object} params
 * @param {number} params.targetYield (kg per hectare)
 * @param {number} params.hardinParamsA (unitless)
 * @param {number} params.hardinParamsB (unitless)
 * @param {number} params.lowerBound (seeds per hectare) It's the lower limit of the curve
 * @param {number} params.higherBound (seeds per hectare) It's the upper limit of the curve
 * @param {string} params.yieldLabel the key used for the yield values, it allows this function to be used to generate
 * field and zones curves.
 * @returns {DensityCurve[]} results are given in metric units.
 * */
export const calculateDensityCurve = ({
  targetYield,
  hardinParamsA,
  hardinParamsB,
  lowerBound,
  upperBound,
  yieldLabel = 'yield'
}: {
  targetYield: number;
  hardinParamsA: number;
  hardinParamsB: number;
  lowerBound: number;
  upperBound: number;
  yieldLabel: string;
}): DensityCurve => {
  const interval = Math.floor((upperBound - lowerBound) / RESOLUTION);
  const steps = Array.from({ length: RESOLUTION + 1 }, (_, index) => lowerBound + interval * index);

  return steps.map((value) => {
    return {
      plantingRate: value,
      [yieldLabel]: getEstimatedYield(value, targetYield, hardinParamsA, hardinParamsB)
    };
  });
};

/**
 * Converts the density curve to the units of measure of the current user.
 * @param Array<{[label: string]: number}> Density curve
 * @param number cropNominalWeight
 * @returns Array<{[label: string]: number}> Converted density curve
 */
export const convertUnitsOfDensityCurve = (curve: DensityCurve, cropNominalWeight: number): DensityCurve => {
  return curve.map((coordinate) => {
    const plantingRate = convert({
      from: { u: 'seed/ha', q: coordinate.plantingRate },
      to: { u: inferUnit('seed-density').unitKey }
    }).q;
    const yieldLabel = Object.keys(coordinate).filter((key) => key !== 'plantingRate')[0];
    const yieldValue = convertYieldWithNominalWeight({
      from: { u: 'kg/hectare', q: coordinate[yieldLabel] },
      toUnit: inferUnit('area-density.yield').unitKey,
      cropNominalWeight: cropNominalWeight
    }).q;
    return {
      plantingRate,
      [yieldLabel]: yieldValue
    };
  });
};
